import { useEffect, useMemo, useState } from 'react';
import { ChevronRight, Search } from 'lucide-react';
import { SortHeader } from '@/pages/MetaFlow/components/SortHeader';
import { compareBy, useTableSort } from '@/pages/MetaFlow/components/table_sort';
import type { NavEntry } from '@/types/navigation-report';
import { fmtNav } from './format';

interface EntriesTableProps {
    items: NavEntry[];
    onSelect: (path: string) => void;
}

const TD = 'px-2 py-2 text-right tabular-nums text-foreground/90';
const INITIAL_VISIBLE = 10;
const VISIBLE_STEPS = [10, 25, 50, Infinity];

const COLS = [
    { key: 'path', label: 'Página de entrada', textual: true, align: 'left' as const },
    { key: 'sessions', label: 'Sessões' },
    { key: 'visitors', label: 'Visitantes' },
    { key: 'advanced', label: 'Avançaram' },
    { key: 'advance_rate_pct', label: 'Taxa de avanço' },
];

/** Páginas de entrada (P1): busca por trecho do path, ordenação por coluna, clique abre o detalhe. */
export function EntriesTable({ items, onSelect }: EntriesTableProps) {
    const { sortKey, sortDir, toggleSort } = useTableSort();
    const [search, setSearch] = useState('');
    const [visibleCount, setVisibleCount] = useState<number>(INITIAL_VISIBLE);
    useEffect(() => setVisibleCount(INITIAL_VISIBLE), [items, search]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        return q ? items.filter((e) => e.path.toLowerCase().includes(q)) : items;
    }, [items, search]);

    const sorted = useMemo(() => {
        if (!sortKey) return filtered;
        return [...filtered].sort((a, b) => compareBy((a as any)[sortKey], (b as any)[sortKey], sortDir));
    }, [filtered, sortKey, sortDir]);

    const visible = sorted.slice(0, visibleCount);
    const hidden = Math.max(sorted.length - visible.length, 0);
    const showMore = () => setVisibleCount((v) => VISIBLE_STEPS.find((step) => step > v) ?? Infinity);

    return (
        <div className="rounded-lg border border-border bg-card">
            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border px-4 py-3">
                <p className="text-[11px] font-medium uppercase tracking-[0.12em] text-muted-foreground">
                    Páginas de entrada <span className="text-foreground/30">· mostrando {fmtNav.num(visible.length)} de {fmtNav.num(sorted.length)}</span>
                </p>
                <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <Search className="h-3.5 w-3.5" />
                    <input
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Buscar path…"
                        aria-label="Buscar página de entrada"
                        className="h-8 w-48 rounded-md border border-border bg-background px-2 text-xs text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    />
                </label>
            </div>
            <div className="overflow-x-auto font-mono">
                <table className="w-full min-w-[720px] text-xs">
                    <thead>
                        <tr className="border-b border-border">
                            {COLS.map((c) => (
                                <th key={c.key} className={c.align === 'left' ? 'px-2 py-2 text-left' : 'px-2 py-2 text-right'}>
                                    <SortHeader label={c.label} sortKey={c.key} activeKey={sortKey} dir={sortDir} onSort={toggleSort} textual={c.textual} align={c.align || 'right'} />
                                </th>
                            ))}
                            <th className="w-8" />
                        </tr>
                    </thead>
                    <tbody>
                        {visible.map((row) => (
                            <tr key={row.path} className="cursor-pointer border-b border-border/60 transition-colors hover:bg-accent/60" onClick={() => onSelect(row.path)} title="Abrir o detalhe desta entrada">
                                <td className="max-w-[420px] px-2 py-2 text-left">
                                    <span className="block truncate font-medium text-foreground" title={row.path}>{row.path}</span>
                                </td>
                                <td className={`${TD} font-medium text-foreground`}>{fmtNav.num(row.sessions)}</td>
                                <td className={TD}>{fmtNav.num(row.visitors)}</td>
                                <td className={TD}>{fmtNav.num(row.advanced)}</td>
                                <td className={TD}>
                                    <span className="flex items-center justify-end gap-2">
                                        <span className="h-1.5 w-20 overflow-hidden rounded-full bg-border">
                                            <span className="block h-full rounded-full bg-emerald-500" style={{ width: `${Math.min(row.advance_rate_pct, 100)}%` }} />
                                        </span>
                                        <span className="w-12">{fmtNav.pct1(row.advance_rate_pct)}</span>
                                    </span>
                                </td>
                                <td className="px-2 text-muted-foreground"><ChevronRight className="h-3.5 w-3.5" /></td>
                            </tr>
                        ))}
                        {visible.length === 0 && (
                            <tr><td colSpan={COLS.length + 1} className="px-2 py-6 text-center text-muted-foreground">Nenhuma entrada encontrada</td></tr>
                        )}
                    </tbody>
                </table>
            </div>
            {(hidden > 0 || visibleCount > INITIAL_VISIBLE) && (
                <div className="flex items-center justify-center gap-2 border-t border-border px-4 py-3">
                    {hidden > 0 && (
                        <button type="button" onClick={showMore} className="rounded-md border border-border px-3 py-1.5 text-xs text-muted-foreground hover:bg-accent hover:text-foreground">
                            Mostrar mais ({fmtNav.num(hidden)} ocultas)
                        </button>
                    )}
                    {visibleCount > INITIAL_VISIBLE && (
                        <button type="button" onClick={() => setVisibleCount(INITIAL_VISIBLE)} className="rounded-md px-3 py-1.5 text-xs text-muted-foreground hover:text-foreground">
                            Mostrar menos
                        </button>
                    )}
                </div>
            )}
        </div>
    );
}
